import React, { useState, useEffect } from "react";
import styled, { keyframes, css, useTheme } from "styled-components";
import {
  GridCol,
  GridRow,
  Header,
  Subheader,
  ButtonText,
  Button,
  Link,
} from "../components";
import { layout, space, flexbox, position, color } from "styled-system";
import AnimationImg from "../assets/ellie_1080.mp4";
import Logo from "../assets/logo.png";
import Dribbble from "../assets/dribbble.png";
import Instagram from "../assets/instagram.png";
import Etsy from "../assets/etsy.png";
import Youtube from "../assets/youtube.png";
import Image1 from "../assets/1.gif";
import Image2 from "../assets/2.gif";
import Image3 from "../assets/3.gif";
import MySweetChubs from "../assets/mysweetchubs.png";

const fadeIn = keyframes`
  0% { 
    opacity: 0;
  } 
  100% { 
    opacity: 1;
  }
`;

const riseIn = keyframes`
  0% { 
    opacity: 0;
    transform: translateY(100%);
  } 
  100% { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const Animation = styled.video`
  visibility: hidden;
  @media ${"only screen and (min-width: 1024px)"} {
    visibility: visible;
  }
  height: auto;
  width: 100%;
  z-index: -1000;
  opacity: 0;

  animation: ${({ animation, animationLength }) =>
    animation &&
    animationLength &&
    css`
      ${animation} ${animationLength} ${({ theme }) => theme.cubicBezier}
    `};
  animation-delay: ${({ animationDelay }) =>
    animationDelay &&
    css`
      ${animationDelay}
    `};
  animation-fill-mode: forwards;
`;

const Hero = styled.div`
  visibility: hidden;
  @media ${"only screen and (min-width: 1024px)"} {
    visibility: visible;
  }
  transition: background-color 0.25s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  position: fixed;
  top: 0px;
  right: 0px;
  left: 0px;
  ${space};
`;

const Image = styled.img`
  ${layout};
  ${space};
  ${position};
  ${color};
  animation: ${({ animation, animationLength }) =>
    animation &&
    animationLength &&
    css`
      ${animation} ${animationLength} ${({ theme }) => theme.cubicBezier}
    `};
  animation-delay: ${({ animationDelay }) =>
    animationDelay &&
    css`
      ${animationDelay}
    `};
  animation-fill-mode: forwards;
`;

const Container = styled.div`
  display: inline-flex;
  transition: opacity 0.25s cubic-bezier(0.165, 0.84, 0.44, 1);

  ${space};
  ${layout};
  ${flexbox};
  ${color};

  animation: ${({ animation, animationLength }) =>
    animation &&
    animationLength &&
    css`
      ${animation} ${animationLength} ${({ theme }) => theme.cubicBezier}
    `};
  animation-delay: ${({ animationDelay }) =>
    animationDelay &&
    css`
      ${animationDelay}
    `};
  animation-fill-mode: forwards;
`;

const grow = keyframes`
    0% { transform: scale(0.25); visibility: visible;}
    25% { transform: scale(0.5); visibility: visible;}
    75% { transform: scale(0.25); visibility: visible;}
    100% { transform: scale(1); visibility: visible;}
`;

const Shape = styled.svg`
  ${position};
  animation: ${({ animation, animationLength }) =>
    animation &&
    animationLength &&
    css`
      ${animation} ${animationLength} ${({ theme }) => theme.cubicBezier}
    `};
  animation-delay: ${({ animationDelay }) =>
    animationDelay &&
    css`
      ${animationDelay}
    `};
  transform-origin: ${({ transformOrigin }) => transformOrigin};
  fill: ${({ color }) => color};
  visibility: ${({ animation }) => animation && "hidden"};
  animation-fill-mode: forwards;
`;

const Desktop = ({ copy }) => {
  const { colors } = useTheme();
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.pageYOffset) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <Animation
        autoPlay
        muted
        loop
        animation={fadeIn}
        animationLength="3s"
        animationDelay="1s"
      >
        <source src={"https://firebasestorage.googleapis.com/v0/b/mysweetchubs.appspot.com/o/ellie_1080.mp4?alt=media&token=09902ed1-a292-42ac-a24e-a4f12068e8a3"} type="video/mp4" />
      </Animation>
      <Hero isScrolling={isScrolling} pt={30} pb={30}>
        <Container pr={64} pl={64}>
          <Container display="flex" flexGrow="1" flexBasis="0px">
            <Link
              opacity={0}
              animation={riseIn}
              animationLength="2s"
              animationDelay="1.8s"
              href="/"
            >
              <Image width="100px" src={Logo} />
            </Link>
          </Container>
          <Container
            display="inline-flex"
            margin="0px auto"
            justifyContent="center"
            flexDirection="column"
            flexBasis="0px"
            opacity={isScrolling ? "0" : "1"}
          >
            <Subheader
              shadow
              color={"white"}
              textAlign="center"
              opacity={0}
              animation={riseIn}
              animationLength="2s"
              animationDelay="2s"
            >
              WELCOME TO
            </Subheader>
            <Image
              width={"300px"}
              src={MySweetChubs}
              opacity={0}
              animation={riseIn}
              animationLength="2s"
              animationDelay="2s"
            />
          </Container>
          <Container
            display="flex"
            justifyContent="flex-end"
            flexGrow="1"
            flexBasis="0px"
          >
            <Container flexDirection="row-reverse">
              <Link
                href="https://bit.ly/mysweetchubs-dribbble-website"
                opacity={0}
                animation={riseIn}
                animationLength="2s"
                animationDelay="2.1s"
              >
                <Image height={50} src={Dribbble} />
              </Link>
              <Link
                href="https://bit.ly/mysweetchubs-instagram"
                opacity={0}
                animation={riseIn}
                animationLength="2s"
                animationDelay="2.2s"
              >
                <Image height={50} src={Instagram} />
              </Link>
              <Link
                href="https://bit.ly/mysweetchub-shop"
                opacity={0}
                animation={riseIn}
                animationLength="2s"
                animationDelay="2.3s"
              >
                <Image height={50} src={Etsy} />
              </Link>
              <Link
                href="https://bit.ly/designcodeepisode2"
                opacity={0}
                animation={riseIn}
                animationLength="2s"
                animationDelay="2.4s"
              >
                <Image height={50} src={Youtube} />
              </Link>
            </Container>
          </Container>
        </Container>
      </Hero>
      <Container
        opacity={0}
        mt="-5px"
        width="100%"
        height="75px"
        style={{
          background:
            "linear-gradient(180deg, #FDEECE 0%, rgba(255, 255, 255, 0) 100%)",
        }}
        justifyContent="center"
        animation={fadeIn}
        animationLength="3s"
        animationDelay="1s"
      />
      <GridRow pt={150} pb={150}>
        <GridCol desktop={1} />
        <GridCol desktop={4}>
          <Container flexDirection="column" height="390px">
            <Header pb={10} color={colors.green}>
              {copy.section1.header}
            </Header>
            <Subheader color={colors.green}>
              {copy.section1.subheader}
            </Subheader>
            <Link href="https://bit.ly/mysweetchubs-instagram">
              <Button backgroundColor={colors.lightGreen} mt={30}>
                <ButtonText color={colors.white}>click me!</ButtonText>
              </Button>
            </Link>
          </Container>
        </GridCol>
        <GridCol desktop={1} />
        <GridCol desktop={5} position="relative">
          <Shape
            position="absolute"
            fill={colors.lightGreen}
            top={0}
            left={0}
            height="100%"
            width="100%"
            viewBox="0 0 100 100"
            animation={grow}
            animationDelay={"1.25s"}
            animationLength={"2s"}
          >
            <circle r="50" cx="50" cy="50" />
          </Shape>
          <Image position="absolute" src={Image1} bottom={0} width="100%" />
        </GridCol>
        <GridCol desktop={1} />
      </GridRow>
      <GridRow pb={150}>
        <GridCol desktop={1} />
        <GridCol desktop={5} position="relative">
          <Shape
            position="absolute"
            fill={colors.lightBlue}
            top={0}
            left={0}
            viewBox="0 0 100 100"
            height="100%"
            width="100%"
            animation={grow}
            animationDelay={"1.25s"}
            animationLength={"2s"}
          >
            <circle r="50" cx="50" cy="50" />
          </Shape>
          <Image position="absolute" src={Image2} bottom={0} width="100%" />
        </GridCol>
        <GridCol desktop={1} />
        <GridCol desktop={4}>
          <Container flexDirection="column" height="390px">
            <Header pb={10} color={colors.blue}>
              {copy.section2.header}
            </Header>
            <Subheader color={colors.blue}>{copy.section2.subheader}</Subheader>
            <Link href="https://bit.ly/mysweetchub-shop">
              <Button backgroundColor={colors.lightBlue} mt={30}>
                <ButtonText color={colors.white}>click me!</ButtonText>
              </Button>
            </Link>
          </Container>
        </GridCol>
        <GridCol desktop={1} />
      </GridRow>
      <GridRow pb={150} pb={200}>
        <GridCol desktop={1} />
        <GridCol desktop={4}>
          <Container flexDirection="column" height="390px">
            <Header pb={10} color={colors.coral}>
              {copy.section3.header}
            </Header>
            <Subheader color={colors.coral}>
              {copy.section3.subheader}
            </Subheader>
            <Link href="https://bit.ly/mysweetchubs-dribbble-website">
              <Button backgroundColor={colors.lightCoral} mt={30}>
                <ButtonText color={colors.white}>click me!</ButtonText>
              </Button>
            </Link>
          </Container>
        </GridCol>
        <GridCol desktop={1} />
        <GridCol desktop={5} position="relative">
          <Shape
            position="absolute"
            fill={colors.lightCoral}
            top={0}
            left={0}
            viewBox="0 0 100 100"
            height="100%"
            width="100%"
            animation={grow}
            animationDelay={"1.25s"}
            animationLength={"2s"}
          >
            <circle r="50" cx="50" cy="50" />
          </Shape>
          <Image position="absolute" src={Image3} bottom={0} width="100%" />
        </GridCol>
        <GridCol desktop={1} />
      </GridRow>
      <Container
        pb={75}
        mr={0}
        ml={0}
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{
          background: "linear-gradient(180deg, #FFFFFF 0%, #FFF3CB 100%)",
        }}
      >
        <Header color="brown">come again!</Header>
        <Image pb={10} width="20%" src={Logo} />
      </Container>
    </>
  );
};

export default Desktop;
